import React, { useState } from 'react';

const T = {
  bg:    '#07091a',
  card:  'rgba(255,255,255,0.03)',
  brd:   'rgba(255,255,255,0.08)',
  text:  '#e2e8f0',
  muted: '#94a3b8',
  blue:  '#00d4ff',
  gold:  '#ffd700',
  green: '#00ff88',
  red:   '#ff4444',
  purp:  '#a78bfa',
};

const SECTIONS = [
  { id:'overview',   emoji:'🗺️',  label:'Overview'           },
  { id:'setup',      emoji:'⚙️',  label:'Setup & Start'      },
  { id:'journal',    emoji:'📓',  label:'Trade Journal'      },
  { id:'risk',       emoji:'🛡️',  label:'Risk Manager'       },
  { id:'fib',        emoji:'📐',  label:'Fibonacci'          },
  { id:'ict',        emoji:'🎯',  label:'ICT Module'         },
  { id:'confluence', emoji:'⚡',  label:'Confluence'         },
  { id:'vision',     emoji:'📸',  label:'Chart Vision AI'   },
  { id:'knowledge',  emoji:'🧠',  label:'Knowledge & Train'  },
  { id:'daily',      emoji:'📅',  label:'Daily Recap'        },
  { id:'psychology', emoji:'🧘',  label:'Psychology'         },
  { id:'stats',      emoji:'📈',  label:'Statistics'         },
  { id:'strategy',   emoji:'📋',  label:'Strategy जतन'       },
  { id:'agents',     emoji:'🤖',  label:'AI Agents'          },
  { id:'chat',       emoji:'💬',  label:'JARVIS Chat'        },
  { id:'training',   emoji:'🎓',  label:'JARVIS Training'    },
];

// ── Info box
function InfoBox({ color = T.blue, icon, title, children }) {
  return (
    <div style={{
      background: color + '11',
      borderTop: `1px solid ${color}33`,
      borderRight: `1px solid ${color}33`,
      borderBottom: `1px solid ${color}33`,
      borderLeft: `3px solid ${color}`,
      borderRadius: '10px',
      padding: '14px 16px',
      marginBottom: '14px',
    }}>
      {title && (
        <div style={{ fontSize: '14px', fontWeight: '700', color, marginBottom: '8px' }}>
          {icon && <span style={{ marginRight: '7px' }}>{icon}</span>}{title}
        </div>
      )}
      <div style={{ fontSize: '14px', color: T.text, lineHeight: '1.7' }}>{children}</div>
    </div>
  );
}

// ── Step
function Step({ n, children }) {
  return (
    <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', alignItems: 'flex-start' }}>
      <div style={{
        width: '26px', height: '26px', borderRadius: '50%', flexShrink: 0,
        background: 'rgba(0,212,255,0.15)', border: '1px solid rgba(0,212,255,0.3)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '12px', fontWeight: '800', color: T.blue,
      }}>{n}</div>
      <div style={{ fontSize: '14px', color: T.text, lineHeight: '1.65', paddingTop: '3px' }}>{children}</div>
    </div>
  );
}

// ── Slider explainer
function SliderCard({ icon, name, range, marathi, tip }) {
  return (
    <div style={{
      background: T.card, border: `1px solid ${T.brd}`,
      borderRadius: '10px', padding: '14px', marginBottom: '10px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
        <span style={{ fontSize: '20px' }}>{icon}</span>
        <div>
          <div style={{ fontSize: '14px', fontWeight: '700', color: T.blue }}>{name}</div>
          <div style={{ fontSize: '12px', color: T.muted }}>{range}</div>
        </div>
      </div>
      <div style={{ fontSize: '14px', color: T.text, lineHeight: '1.65', marginBottom: '6px' }}>{marathi}</div>
      {tip && <div style={{ fontSize: '13px', color: T.gold }}>💡 {tip}</div>}
    </div>
  );
}

// ── Screen card
function ScreenCard({ emoji, name, path, desc }) {
  return (
    <div style={{
      background: T.card, border: `1px solid ${T.brd}`,
      borderRadius: '10px', padding: '12px 14px', marginBottom: '8px',
      display: 'flex', gap: '12px', alignItems: 'flex-start',
    }}>
      <span style={{ fontSize: '22px', flexShrink: 0 }}>{emoji}</span>
      <div>
        <div style={{ fontSize: '14px', fontWeight: '700', color: T.blue }}>{name}</div>
        <div style={{ fontSize: '12px', color: T.gold, marginBottom: '4px' }}>Sidebar → {path}</div>
        <div style={{ fontSize: '13px', color: T.text, lineHeight: '1.6' }}>{desc}</div>
      </div>
    </div>
  );
}

// ── Section content
function SectionContent({ id }) {
  switch (id) {

    // ────────────────────────────────────────────────────────────────────
    case 'overview': return (
      <div>
        <InfoBox color={T.blue} icon="🤖" title="JARVIS म्हणजे काय?">
          JARVIS हे तुमचे AI XAU/USD Gold Trading Assistant आहे. हे तुम्हाला trade plan करायला,
          risk manage करायला, आणि ICT/SMC setups analyze करायला मदत करते. सगळे features
          Sidebar मधून access करता येतात.
        </InfoBox>

        <div style={{ fontSize: '13px', fontWeight: '700', color: T.muted, letterSpacing: '2px', marginBottom: '12px' }}>
          SIDEBAR मधील सर्व Pages
        </div>

        <ScreenCard emoji="🏠" name="Home" path="MAIN → Home"
          desc="मुख्य dashboard. XAU/USD live price, session status, AI confidence, आज चे plan, recent trades आणि JARVIS चे message दिसते." />
        <ScreenCard emoji="💬" name="Chat with JARVIS" path="MAIN → Chat"
          desc="JARVIS ला कोणताही question विचारा. Market analysis, trade setup, rules — सगळ्याबद्दल बोलता येते." />
        <ScreenCard emoji="🖥️" name="Dashboard" path="MAIN → Dashboard"
          desc="तुमची overall performance, evolution stage, top strategies आणि quick stats एका नजरेत दिसतात." />
        <ScreenCard emoji="📓" name="Trade Journal" path="TRADING → Trade Journal"
          desc="प्रत्येक trade नोंदवा — entry, exit, SL, TP, grade, result. हे data सगळ्या analysis साठी वापरले जाते." />
        <ScreenCard emoji="🛡️" name="Risk Manager" path="TRADING → Risk Manager"
          desc="Position size calculator. Account size, risk %, SL distance टाका — lot size आणि dollar risk automatically calculate होतो." />
        <ScreenCard emoji="📈" name="Statistics" path="TRADING → Statistics"
          desc="Win rate, best sessions, best setups, equity curve — तुमच्या trading चे detailed stats." />
        <ScreenCard emoji="🧘" name="Psychology" path="TRADING → Psychology"
          desc="Daily mood track करा. Emotional patterns analyze करा. Discipline score track करा." />
        <ScreenCard emoji="🎯" name="ICT / SMC" path="ANALYSIS → ICT / SMC"
          desc="Order Blocks, FVGs, Liquidity Levels, Market Structure नोंदवा. JARVIS हे analyze करतो." />
        <ScreenCard emoji="📐" name="Fibonacci" path="ANALYSIS → Fibonacci"
          desc="Swing High/Low टाका — JARVIS तुमचे personal fib levels (0.15, 0.75, 0.84, 0.90) calculate करतो." />
        <ScreenCard emoji="⚡" name="Confluence Scorer" path="ANALYSIS → Confluence"
          desc="Setup चे सर्व factors check करा — JARVIS A/B/C/D grade देतो आणि trade घ्यायचा की नाही ते सांगतो." />
        <ScreenCard emoji="🤖" name="Agent Network" path="ANALYSIS → Agent Network"
          desc="12 AI agents एकत्र analysis करतात. 'Should I trade now?' विचारा — सर्व agents मिळून final verdict देतात." />
        <ScreenCard emoji="📚" name="Daily Teaching" path="LEARN → Daily Teaching"
          desc="रोज एक ICT/SMC concept शिका. Mood track करा. JARVIS तुम्हाला personalized lesson देतो." />
        <ScreenCard emoji="🧠" name="Knowledge Base" path="LEARN → Knowledge Base"
          desc="तुमची trading strategy, notes, rules येथे save करा. ICT reference library पण येथे आहे." />
        <ScreenCard emoji="📋" name="Rule Book" path="LEARN → Rule Book"
          desc="Trading rules add करा. JARVIS trade घेण्यापूर्वी हे rules check करतो." />

        <InfoBox color={T.green} icon="✅" title="सुरुवात कशी करायची?">
          <Step n="1">Risk Manager मध्ये account size आणि risk % set करा</Step>
          <Step n="2">Rule Book मध्ये तुमचे 3-5 main rules टाका</Step>
          <Step n="3">Knowledge Base मध्ये तुमची strategy लिहा</Step>
          <Step n="4">Trade घेताना Confluence Scorer मध्ये setup check करा</Step>
          <Step n="5">Trade नंतर Trade Journal मध्ये entry नोंदवा</Step>
        </InfoBox>
      </div>
    );

    // ────────────────────────────────────────────────────────────────────
    case 'journal': return (
      <div>
        <InfoBox color={T.gold} icon="📓" title="Trade Journal कशासाठी?">
          प्रत्येक trade चा record ठेवणे खूप महत्त्वाचे आहे. हे data JARVIS Statistics, Agent Network
          आणि Dashboard मध्ये automatically वापरले जाते. Trade नोंदवला नाही तर AI analysis कमकुवत होते.
        </InfoBox>

        <div style={{ fontSize: '13px', fontWeight: '700', color: T.muted, letterSpacing: '2px', marginBottom: '12px' }}>
          Trade नोंदवण्याचे Steps
        </div>
        <Step n="1"><strong style={{color:T.blue}}>Type</strong> — BUY किंवा SELL निवडा</Step>
        <Step n="2"><strong style={{color:T.blue}}>Entry Price</strong> — ज्या किमतीला trade घेतली ती लिहा (उदा. 3285.50)</Step>
        <Step n="3"><strong style={{color:T.blue}}>Stop Loss (SL)</strong> — तुमचा stop loss price लिहा</Step>
        <Step n="4"><strong style={{color:T.blue}}>Take Profit (TP)</strong> — तुमचे target price लिहा</Step>
        <Step n="5"><strong style={{color:T.blue}}>Lot Size</strong> — किती lots घेतले ते लिहा (Risk Manager वापरा!)</Step>
        <Step n="6"><strong style={{color:T.blue}}>Setup</strong> — कोणत्या setup वर trade घेतली? (Order Block, FVG, Fibonacci, etc.)</Step>
        <Step n="7"><strong style={{color:T.blue}}>Session</strong> — London, NY, Asian — कोणत्या session मध्ये?</Step>
        <Step n="8"><strong style={{color:T.blue}}>Grade</strong> — स्वतः judge करा — A+, A, B, C, D</Step>
        <Step n="9"><strong style={{color:T.blue}}>Notes</strong> — का घेतली, काय दिसले ते थोडक्यात लिहा</Step>

        <InfoBox color={T.green} icon="📸" title="Result Update करणे">
          Trade close झाल्यावर Journal मध्ये जा → त्या trade वर click करा → Result (WIN/LOSS/BE) update करा.
          हे mandatory आहे — नाहीतर Statistics चुकीचे दिसतात.
        </InfoBox>

        <InfoBox color={T.purp} icon="⭐" title="Grade म्हणजे काय?">
          <div>A+ = Elite setup — सर्व conditions perfect</div>
          <div>A = Strong setup — बहुतेक conditions fulfilled</div>
          <div>B = Average — काही conditions missing</div>
          <div>C = Weak — घ्यायला नको होती</div>
          <div>D = FOMO / revenge trade — चुकीची trade</div>
        </InfoBox>

        <InfoBox color={T.blue} icon="📤" title="MT4/MT5 मधून Import">
          LEARN → MT4/MT5 Import मध्ये जा. तुमच्या broker चा trade history export करा (CSV format मध्ये)
          आणि येथे upload करा — सर्व trades automatically journal मध्ये येतात.
        </InfoBox>
      </div>
    );

    // ────────────────────────────────────────────────────────────────────
    case 'risk': return (
      <div>
        <InfoBox color={T.red} icon="🛡️" title="Risk Manager — Sliders काय करतात?">
          हे tool position size calculate करते. Trading मध्ये सर्वात महत्त्वाचे म्हणजे risk management.
          खाली प्रत्येक slider चे काम समजावून सांगितले आहे.
        </InfoBox>

        <SliderCard
          icon="💰" name="Account Balance"
          range="$100 — $100,000"
          marathi="तुमच्या broker account मध्ये किती USD आहे ते येथे set करा. उदाहरण: तुमचे account $5,000 चे असेल तर 5000 टाका. हे value Settings मध्ये save होते."
          tip="दर महिन्याला account balance update करा"
        />
        <SliderCard
          icon="⚖️" name="Risk Per Trade (%)"
          range="0.5% — 5%"
          marathi="प्रत्येक trade मध्ये तुम्ही account चे किती % risk करायला तयार आहात? नवीन traders साठी 1-2% शिफारस आहे. $5,000 account वर 1% = $50 risk per trade."
          tip="Beginners साठी 1% — Professional साठी 2% max"
        />
        <SliderCard
          icon="📏" name="Stop Loss (pips/points)"
          range="5 — 500 pips"
          marathi="तुमचा stop loss Entry Price पासून किती pips दूर आहे? उदाहरण: Entry 3285, SL 3280 म्हणजे 5 pips SL. हे value टाकल्यावर JARVIS automatically lot size calculate करतो."
          tip="XAU/USD साठी 1 pip = $0.1 per 0.01 lot"
        />
        <SliderCard
          icon="🎯" name="Risk/Reward Ratio (RR)"
          range="1:1 — 1:10"
          marathi="1:2 म्हणजे तुम्ही $50 risk करून $100 कमवण्याचा target ठेवता. ICT methodology नुसार minimum 1:2 RR असावे. JARVIS TP price automatically suggest करतो."
          tip="Minimum 1:2 RR शिवाय trade घेऊ नका"
        />

        <div style={{ fontSize: '13px', fontWeight: '700', color: T.muted, letterSpacing: '2px', margin: '18px 0 12px' }}>
          RISK MANAGER कसा वापरायचा — Steps
        </div>
        <Step n="1">Account Balance slider account size वर set करा</Step>
        <Step n="2">Risk % तुमच्या comfort प्रमाणे set करा (शिफारस: 1%)</Step>
        <Step n="3">Current trade चा SL (pips मध्ये) टाका</Step>
        <Step n="4">JARVIS आपोआप <strong style={{color:T.green}}>Lot Size</strong> आणि <strong style={{color:T.red}}>Dollar Risk</strong> दाखवेल</Step>
        <Step n="5">तो lot size trade मध्ये वापरा — कधीही जास्त घेऊ नका</Step>

        <InfoBox color={T.gold} icon="💡" title="XAU/USD साठी Quick Rule">
          $5,000 account, 1% risk, 10 pips SL → Lot size ≈ 0.05 lots (5 micro lots).
          Risk Manager आपोआप हे calculate करतो — manually करायची गरज नाही.
        </InfoBox>
      </div>
    );

    // ────────────────────────────────────────────────────────────────────
    case 'fib': return (
      <div>
        <InfoBox color={T.blue} icon="📐" title="Fibonacci Calculator कशासाठी?">
          Fibonacci retracement हे ICT/SMC मधील एक महत्त्वाचे tool आहे. तुमचे personal fib levels
          (BUY: 0.15 & 0.75 / SELL: 0.84 & 0.90) येथे automatically calculate होतात.
        </InfoBox>

        <div style={{ fontSize: '13px', fontWeight: '700', color: T.muted, letterSpacing: '2px', marginBottom: '12px' }}>
          Fibonacci कसा वापरायचा
        </div>
        <Step n="1">
          <strong style={{color:T.blue}}>Swing High</strong> — Recent high price टाका.
          BUY setup साठी: latest swing high. SELL setup साठी: latest swing high.
          (उदाहरण: 3300.00)
        </Step>
        <Step n="2">
          <strong style={{color:T.blue}}>Swing Low</strong> — Recent low price टाका.
          (उदाहरण: 3260.00)
        </Step>
        <Step n="3">
          <strong style={{color:T.green}}>BUY किंवा SELL</strong> button click करा
        </Step>
        <Step n="4">
          JARVIS तुमचे personal levels calculate करेल:
          BUY → <strong style={{color:T.green}}>0.15 level (Premium BUY zone)</strong> आणि <strong style={{color:T.green}}>0.75 level (Deep OTE BUY zone)</strong>
        </Step>
        <Step n="5">
          Chart वर हे levels draw करा आणि price येण्याची वाट पाहा
        </Step>

        <InfoBox color={T.green} icon="🎯" title="तुमचे Personal Fib Levels">
          <div style={{ marginBottom: '8px' }}>
            <strong style={{color:T.green}}>BUY Zones:</strong>
          </div>
          <div>• <strong>0.15 level</strong> = Swing range च्या 15% pullback — Premium BUY zone (High probability)</div>
          <div style={{ marginBottom: '10px' }}>• <strong>0.75 level</strong> = 75% deep retracement — OTE (Optimal Trade Entry) zone</div>
          <div style={{ marginBottom: '8px' }}>
            <strong style={{color:T.red}}>SELL Zones:</strong>
          </div>
          <div>• <strong>0.84 level</strong> = 84% retracement — Premium SELL zone</div>
          <div>• <strong>0.90 level</strong> = 90% deep — High probability SELL</div>
        </InfoBox>

        <InfoBox color={T.gold} icon="💡" title="OTE Zone म्हणजे काय?">
          OTE = Optimal Trade Entry. ICT methodology मध्ये 0.62 ते 0.79 हे zone
          सर्वात high probability entry साठी आहे. तुमचा 0.75 level या zone मध्येच येतो.
        </InfoBox>
      </div>
    );

    // ────────────────────────────────────────────────────────────────────
    case 'ict': return (
      <div>
        <InfoBox color={T.purp} icon="🎯" title="ICT Module म्हणजे काय?">
          ICT (Inner Circle Trader) / SMC (Smart Money Concepts) setups येथे नोंदवा.
          हे data JARVIS Agent Network मध्ये automatically वापरले जाते analysis साठी.
          4 tabs आहेत — प्रत्येक setup type साठी एक.
        </InfoBox>

        <div style={{ fontSize: '14px', fontWeight: '700', color: T.blue, marginBottom: '10px', marginTop: '4px' }}>
          📦 Tab 1: Order Blocks (OB)
        </div>
        <Step n="1">Timeframe निवडा (M15, H1, H4, D1)</Step>
        <Step n="2">Type: Bullish OB (BUY साठी) किंवा Bearish OB (SELL साठी)</Step>
        <Step n="3">High Price आणि Low Price — OB candle ची high/low टाका</Step>
        <Step n="4">Status: Active (untouched), Mitigated (price touched it), Swept (invalidated)</Step>
        <Step n="5">Save करा — हे JARVIS analysis मध्ये वापरेल</Step>

        <InfoBox color={T.blue} icon="💡" title="Order Block म्हणजे काय?">
          Price ज्या candle मधून strong move झाला त्या last opposite candle ला OB म्हणतात.
          Bullish OB: Strong up move च्या आधीची last bearish candle.
          Bearish OB: Strong down move च्या आधीची last bullish candle.
        </InfoBox>

        <div style={{ fontSize: '14px', fontWeight: '700', color: T.gold, marginBottom: '10px', marginTop: '16px' }}>
          🔷 Tab 2: Fair Value Gaps (FVG)
        </div>
        <Step n="1">Direction: Bullish FVG (gap up) किंवा Bearish FVG (gap down)</Step>
        <Step n="2">Gap High आणि Gap Low — FVG च्या range च्या किमती टाका</Step>
        <Step n="3">Status: Open (unfilled), Partially Filled, Filled</Step>

        <InfoBox color={T.gold} icon="💡" title="FVG म्हणजे काय?">
          3 candles च्या मध्ये जो gap तयार होतो तो FVG आहे. Candle 1 चा high आणि
          Candle 3 चा low यांच्यामध्ये price return करते — हे BUY/SELL zone आहे.
        </InfoBox>

        <div style={{ fontSize: '14px', fontWeight: '700', color: T.green, marginBottom: '10px', marginTop: '16px' }}>
          💧 Tab 3: Liquidity Levels
        </div>
        <Step n="1">Type: Buy-side Liquidity (equal highs) किंवा Sell-side Liquidity (equal lows)</Step>
        <Step n="2">Price Level — ती level कोणत्या price ला आहे ते टाका</Step>
        <Step n="3">Timeframe निवडा</Step>

        <div style={{ fontSize: '14px', fontWeight: '700', color: T.red, marginBottom: '10px', marginTop: '16px' }}>
          🏗️ Tab 4: Market Structure
        </div>
        <Step n="1">Event: BOS (Break of Structure) किंवा CHoCH (Change of Character)</Step>
        <Step n="2">Direction: Bullish किंवा Bearish</Step>
        <Step n="3">BOS Price — ज्या level वर break झाला ती price</Step>
      </div>
    );

    // ────────────────────────────────────────────────────────────────────
    case 'confluence': return (
      <div>
        <InfoBox color={T.purp} icon="⚡" title="Confluence Scorer म्हणजे काय?">
          Trade घेण्यापूर्वी हे tool वापरा. सर्व factors check करा — JARVIS A+ ते D पर्यंत
          grade देतो आणि trade घ्यायची की नाही ते clearly सांगतो. B grade पेक्षा कमी असेल
          तर trade skip करा.
        </InfoBox>

        <div style={{ fontSize: '13px', fontWeight: '700', color: T.muted, letterSpacing: '2px', marginBottom: '12px' }}>
          Confluence Scorer कसा वापरायचा
        </div>
        <Step n="1">New Analysis button click करा</Step>
        <Step n="2">Setup मध्ये trade description लिहा (उदा. "H1 Bullish OB + FVG at 3275")</Step>
        <Step n="3">खालील factors check/uncheck करा — प्रत्येक factor जो present आहे तो tick करा</Step>
        <Step n="4">Score Calculate button click करा</Step>
        <Step n="5">JARVIS grade दाखवेल — <strong style={{color:T.green}}>A/A+ = Trade घ्या</strong>, <strong style={{color:T.red}}>C/D = Skip करा</strong></Step>

        <div style={{ fontSize: '13px', fontWeight: '700', color: T.muted, letterSpacing: '2px', margin: '16px 0 12px' }}>
          Factors म्हणजे काय? — प्रत्येकाचे meaning
        </div>

        {[
          { icon:'📊', name:'HTF Bias', desc:'Higher Timeframe (D1/W1) वर price कोणत्या direction ला जाण्याची शक्यता आहे? तुमचा trade त्याच direction मध्ये असेल तर हे tick करा.' },
          { icon:'🏗️', name:'Market Structure', desc:'BOS (Break of Structure) झाला आहे का तुमच्या trade direction मध्ये? CHoCH झाला असेल तरी tick करा.' },
          { icon:'📦', name:'Order Block', desc:'Price एखाद्या valid Order Block जवळ आहे का? Active, unmitigated OB असेल तर tick करा.' },
          { icon:'🔷', name:'Fair Value Gap (FVG)', desc:'Unfilled FVG present आहे का? Price FVG मध्ये येऊन entry घेत असेल तर tick करा.' },
          { icon:'💧', name:'Liquidity Sweep', desc:'Price ने recent high/low sweep केला का? (Stop hunt झाला का?) Sweep नंतर reversal येणे high probability.' },
          { icon:'📐', name:'Fibonacci OTE Zone', desc:'Price तुमच्या fib levels (0.75 BUY किंवा 0.84/0.90 SELL) जवळ आहे का?' },
          { icon:'⏰', name:'Session Alignment', desc:'London किंवा NY session मध्ये trade आहे का? Asian session मध्ये avoid करा.' },
          { icon:'📰', name:'No Major News', desc:'अगदी 30 मिनिटांत कोणती major news नाही? (FOMC, NFP, CPI) असल्यास tick करू नका.' },
          { icon:'🕐', name:'Kill Zone Entry', desc:'London Open (8:30-11 IST) किंवा NY Open (18:30-21 IST) या time मध्ये entry आहे का?' },
        ].map(f => (
          <div key={f.name} style={{
            background: T.card, border: `1px solid ${T.brd}`,
            borderRadius: '8px', padding: '12px 14px', marginBottom: '8px',
            display: 'flex', gap: '10px',
          }}>
            <span style={{ fontSize: '18px', flexShrink: 0 }}>{f.icon}</span>
            <div>
              <div style={{ fontSize: '14px', fontWeight: '700', color: T.blue, marginBottom: '4px' }}>{f.name}</div>
              <div style={{ fontSize: '13px', color: T.text, lineHeight: '1.6' }}>{f.desc}</div>
            </div>
          </div>
        ))}

        <InfoBox color={T.green} icon="🏆" title="Grade System">
          <div>🟢 <strong>A+ (8.0+)</strong> = ELITE setup — जरूर trade घ्या</div>
          <div>🔵 <strong>A (6.5-8.0)</strong> = STRONG — trade घेण्यास योग्य</div>
          <div>🟡 <strong>B (5.0-6.5)</strong> = DECENT — caution ने घ्या</div>
          <div>🟠 <strong>C (3.0-5.0)</strong> = WEAK — skip करा</div>
          <div>🔴 <strong>D (0-3.0)</strong> = SKIP — नाही घ्यायची</div>
        </InfoBox>
      </div>
    );

    // ────────────────────────────────────────────────────────────────────
    case 'strategy': return (
      <div>
        <InfoBox color={T.gold} icon="📋" title="Strategy / Script कुठे ठेवायची?">
          तुमची trading strategy, setups, scripts — या सर्व गोष्टी <strong style={{color:T.gold}}>2 ठिकाणी</strong> save करता येतात:
        </InfoBox>

        <div style={{ fontSize: '14px', fontWeight: '700', color: T.blue, marginBottom: '10px' }}>
          📍 Option 1: Knowledge Base (शिफारस)
        </div>
        <Step n="1">Sidebar → LEARN → Knowledge Base मध्ये जा</Step>
        <Step n="2">"My Notes" tab वर click करा</Step>
        <Step n="3">"Add Note" button click करा</Step>
        <Step n="4">Title द्या (उदा. "My XAU/USD Strategy")</Step>
        <Step n="5">Category निवडा: Strategy, Setup, Rule, Observation</Step>
        <Step n="6">तुमची पूर्ण strategy लिहा — price action rules, entry criteria, exit rules</Step>
        <Step n="7">Save करा — JARVIS Chat मध्ये तुम्ही या strategy बद्दल विचारू शकता</Step>

        <div style={{ fontSize: '14px', fontWeight: '700', color: T.purp, marginBottom: '10px', marginTop: '16px' }}>
          📍 Option 2: Rule Book
        </div>
        <Step n="1">Sidebar → LEARN → Rule Book मध्ये जा</Step>
        <Step n="2">"Add Rule" button click करा</Step>
        <Step n="3">Rule लिहा — specific आणि actionable असायला हवे</Step>
        <Step n="4">Category: Entry, Exit, Risk, Psychology, Strategy</Step>
        <Step n="5">Priority: High/Medium/Low set करा</Step>

        <InfoBox color={T.green} icon="💡" title="Script (Trading Script) म्हणजे काय?">
          जर तुमच्याकडे Pine Script (TradingView) किंवा MQL4/5 (MetaTrader) script असेल:
          <div style={{ marginTop: '8px' }}>
            • Knowledge Base → My Notes → New Note → Category: "Strategy" → Script paste करा
          </div>
          <div style={{ marginTop: '4px' }}>
            • नंतर JARVIS Chat मध्ये "माझी strategy analyze कर" असे विचारा
          </div>
        </InfoBox>

        <InfoBox color={T.blue} icon="📥" title="PDF / YouTube Import">
          Sidebar → LEARN → YouTube / PDF मध्ये:
          <div style={{ marginTop: '8px' }}>• YouTube link टाका — JARVIS key concepts extract करेल</div>
          <div style={{ marginTop: '4px' }}>• PDF upload करा — Trading books/courses automatically Knowledge Base मध्ये जातात</div>
        </InfoBox>

        <InfoBox color={T.gold} icon="🔗" title="Broker Connect">
          Sidebar → LEARN → Broker Connect मध्ये Broker API key/details add करा.
          हे future updates मध्ये direct trade execution साठी वापरले जाईल.
        </InfoBox>
      </div>
    );

    // ────────────────────────────────────────────────────────────────────
    case 'agents': return (
      <div>
        <InfoBox color={T.blue} icon="🤖" title="AI Agent Network म्हणजे काय?">
          JARVIS मध्ये 12+ AI agents आहेत जे एकत्र काम करतात. तुम्ही एक question विचारल्यावर
          सर्व agents आपापले analysis करतात आणि एकत्र final verdict देतात.
        </InfoBox>

        <div style={{ fontSize: '13px', fontWeight: '700', color: T.muted, letterSpacing: '2px', marginBottom: '12px' }}>
          ANALYSIS → Agent Network — कसा वापरायचा
        </div>
        <Step n="1">Sidebar → ANALYSIS → Agent Network मध्ये जा</Step>
        <Step n="2">Question box मध्ये विचारा — खालील examples वापरा:</Step>

        {[
          '"Should I trade right now?"',
          '"Is this a high-probability setup?"',
          '"What is the best entry level for XAU/USD?"',
          '"Should I manage my open trade?"',
          '"मी आत्ता trade घ्यावी का?"',
        ].map((q,i) => (
          <div key={i} style={{
            background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.2)',
            borderRadius: '8px', padding: '8px 14px', marginBottom: '8px',
            fontSize: '13px', color: T.blue, fontStyle: 'italic',
          }}>{q}</div>
        ))}

        <Step n="3">Run Analysis button click करा</Step>
        <Step n="4">12 agents real-time analysis करताना दिसतील</Step>
        <Step n="5">Final verdict येईल: BUY / SELL / WAIT</Step>

        <div style={{ fontSize: '13px', fontWeight: '700', color: T.muted, letterSpacing: '2px', margin: '16px 0 12px' }}>
          कोणते Agents काय करतात?
        </div>
        {[
          { icon:'📊', name:'A_LivePrice', desc:'Live XAU/USD price, change, session status fetch करतो' },
          { icon:'📐', name:'A_Fibonacci', desc:'तुमचे personal fib levels (0.15/0.75/0.84/0.90) check करतो' },
          { icon:'⚡', name:'A_Confluence', desc:'सर्व factors एकत्र करून confluence score देतो' },
          { icon:'🛡️', name:'A_Risk', desc:'Current risk exposure, open trades check करतो' },
          { icon:'📰', name:'A_News (AI)', desc:'News risk assess करतो — Groq AI वापरतो' },
          { icon:'🧘', name:'A_Psychology', desc:'तुमचा recent mood/psychology track करतो' },
          { icon:'📈', name:'A_Statistics', desc:'Win rate, best sessions, patterns analyze करतो' },
        ].map(a => (
          <div key={a.name} style={{
            background: T.card, border: `1px solid ${T.brd}`,
            borderRadius: '8px', padding: '10px 14px', marginBottom: '6px',
            display: 'flex', gap: '10px', alignItems: 'flex-start',
          }}>
            <span style={{ fontSize: '16px', flexShrink: 0 }}>{a.icon}</span>
            <div>
              <div style={{ fontSize: '13px', fontWeight: '700', color: T.blue }}>{a.name}</div>
              <div style={{ fontSize: '13px', color: T.text }}>{a.desc}</div>
            </div>
          </div>
        ))}

        <InfoBox color={T.green} icon="👥" title="ANALYSIS → Multi-Agent AI">
          Multi-Agent AI screen मध्ये 5-step sequential analysis होतो:
          Market Analyst → Risk Manager → News Agent → Trade Manager → Master Decision.
          हे Agent Network पेक्षा deeper analysis करतो पण थोडा जास्त वेळ लागतो.
        </InfoBox>
      </div>
    );

    // ────────────────────────────────────────────────────────────────────
    case 'chat': return (
      <div>
        <InfoBox color={T.blue} icon="💬" title="JARVIS Chat — काय विचारता येते?">
          JARVIS ला Marathi किंवा English मध्ये कोणताही trading question विचारा.
          Groq AI (llama-3.3-70b) model वापरतो — expert level responses मिळतात.
        </InfoBox>

        <div style={{ fontSize: '13px', fontWeight: '700', color: T.muted, letterSpacing: '2px', marginBottom: '12px' }}>
          Chat कसा वापरायचा
        </div>
        <Step n="1">Sidebar → MAIN → Chat with JARVIS मध्ये जा</Step>
        <Step n="2">खालील input bar मध्ये question type करा</Step>
        <Step n="3">🎤 Microphone button click करून voice मध्ये बोलता येते (English + Hinglish)</Step>
        <Step n="4">📸 Camera/Screenshot button — chart screenshot attach करता येते</Step>
        <Step n="5">Send करा — JARVIS detailed response देईल</Step>

        <div style={{ fontSize: '13px', fontWeight: '700', color: T.muted, letterSpacing: '2px', margin: '16px 0 12px' }}>
          असे Questions विचारा
        </div>
        {[
          { q:'"XAU/USD आत्ता BUY करावा का?"', note: 'Current analysis' },
          { q:'"Order Block म्हणजे काय? उदाहरणासह सांग"', note: 'Education' },
          { q:'"माझ्या last 10 trades मध्ये काय चुका झाल्या?"', note: 'Review' },
          { q:'"3285 entry, 3275 SL, 1% risk — lot size किती?"', note: 'Calculation' },
          { q:'"London session trade घ्यायला सर्वात चांगला वेळ कोणता?"', note: 'Timing' },
          { q:'"माझी strategy explain कर"', note: 'Knowledge Base मधून' },
        ].map((item,i) => (
          <div key={i} style={{
            background: 'rgba(0,212,255,0.05)', border: '1px solid rgba(0,212,255,0.15)',
            borderRadius: '8px', padding: '10px 14px', marginBottom: '8px',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px',
          }}>
            <div style={{ fontSize: '13px', color: T.text, fontStyle: 'italic', flex: 1 }}>{item.q}</div>
            <div style={{ fontSize: '11px', color: T.gold, whiteSpace: 'nowrap' }}>{item.note}</div>
          </div>
        ))}

        <InfoBox color={T.purp} icon="🎤" title="Bottom Bar (JARVIS Orb)">
          Screen च्या खाली एक animated orb आहे — "Ask JARVIS anything..." input bar.
          हे कोणत्याही page वर असताना directly JARVIS ला message करता येते.
          Mic button click करून voice input पण वापरता येते.
        </InfoBox>

        <InfoBox color={T.gold} icon="⚡" title="Pro Tips">
          <div>• Specific questions विचारा — "BUY करू का?" पेक्षा "3285 OB वर BUY करू का, H1 bullish structure आहे" असे विचारा</div>
          <div style={{ marginTop: '6px' }}>• Chart screenshot attach केल्यास JARVIS visual analysis पण करतो</div>
          <div style={{ marginTop: '6px' }}>• JARVIS कधीही financial advice देत नाही — हे educational tool आहे</div>
        </InfoBox>
      </div>
    );

    // ────────────────────────────────────────────────────────────────────
    case 'setup': return (
      <div>
        <InfoBox color={T.green} icon="⚙️" title="JARVIS Local वर कसा चालवायचा?">
          JARVIS तुमच्या स्वतःच्या computer वर चालतो. Cloud नाही, hosting नाही, monthly charge नाही.
          फक्त एकदा build करा — नंतर रोज एक double-click.
        </InfoBox>

        <div style={{ fontSize: '13px', fontWeight: '700', color: T.muted, letterSpacing: '2px', marginBottom: '12px' }}>
          पहिल्यांदा (एकदाच करायचे)
        </div>
        <Step n="1"><strong style={{color:T.gold}}>JARVIS_BUILD.bat</strong> — Project folder मध्ये हे file दिसेल. Double-click करा. ~1 मिनिट build होतो. एकदाच करायचे.</Step>
        <Step n="2">Build झाल्यावर "BUILD COMPLETE" message येईल. Close करा.</Step>

        <div style={{ fontSize: '13px', fontWeight: '700', color: T.muted, letterSpacing: '2px', margin: '16px 0 12px' }}>
          रोज वापरणे (Daily Use)
        </div>
        <Step n="1"><strong style={{color:T.green}}>JARVIS_START.bat</strong> — Double-click करा. Terminal window येईल (minimize राहील).</Step>
        <Step n="2">3 सेकंदात Browser automatically उघडेल → <strong style={{color:T.blue}}>http://localhost:3001</strong></Step>
        <Step n="3">JARVIS ready! Trading सुरू करा.</Step>
        <Step n="4">दिवस संपल्यावर JARVIS_START.bat चा terminal window close करा.</Step>

        <InfoBox color={T.blue} icon="📱" title="Mobile वर access करायचे? (Same WiFi)">
          JARVIS_START.bat चालवल्यावर terminal मध्ये दिसेल:<br /><br />
          <code style={{color:T.green}}>Mobile (WiFi): http://192.168.x.x:3001</code><br /><br />
          Phone वर Chrome उघडा → तो URL type करा → JARVIS phone वर पण चालतो!
        </InfoBox>

        <InfoBox color={T.gold} icon="💾" title="Data कुठे save होतो?">
          सर्व data तुमच्या Browser च्या localStorage मध्ये save होतो:
          <div style={{ marginTop: '8px' }}>• Rules, Trades, Knowledge, Behaviors — सगळे browser मध्ये</div>
          <div>• Browser clear केला तर data जाऊ शकतो — Settings → Export Backup नियमित करा</div>
          <div>• Internet फक्त AI chat (Groq) आणि Gold Price साठी लागतो</div>
        </InfoBox>

        <InfoBox color={T.red} icon="⚠️" title="Code बदलला तर?">
          Code update केल्यावर <strong style={{color:T.gold}}>JARVIS_BUILD.bat</strong> परत चालवा — नवीन build तयार होईल.
          नंतर JARVIS_START.bat चालवा. रोज करायची गरज नाही — फक्त code बदलल्यावर.
        </InfoBox>
      </div>
    );

    // ────────────────────────────────────────────────────────────────────
    case 'vision': return (
      <div>
        <InfoBox color={T.blue} icon="📸" title="Chart Vision AI म्हणजे काय?">
          TradingView वरचा chart screenshot JARVIS ला द्या — JARVIS त्या chart मधील
          Order Blocks, FVGs, Liquidity, Market Structure automatically ओळखतो आणि
          तुमच्या saved rules apply करून trade plan देतो.
          Groq llama-4-scout (free vision model) वापरतो.
        </InfoBox>

        <div style={{ fontSize: '13px', fontWeight: '700', color: T.muted, letterSpacing: '2px', marginBottom: '12px' }}>
          Chart Vision — 2 मार्ग
        </div>

        <div style={{ fontSize: '14px', fontWeight: '700', color: T.green, marginBottom: '8px' }}>📍 Method 1: Chat मधून (Recommended)</div>
        <Step n="1">MAIN → Chat with JARVIS मध्ये जा</Step>
        <Step n="2">Chat input मध्ये <strong style={{color:T.blue}}>📎 button</strong> click करा</Step>
        <Step n="3">Chart screenshot select करा (PNG/JPG)</Step>
        <Step n="4">Message type करा: <em style={{color:T.gold}}>"या chart मध्ये OB कुठे आहे? माझी Pine Script rule apply होते का?"</em></Step>
        <Step n="5">Send करा — JARVIS तुमचे Rule Book rules वापरून chart analyze करतो</Step>

        <div style={{ fontSize: '14px', fontWeight: '700', color: T.blue, marginBottom: '8px', marginTop: '14px' }}>📍 Method 2: Chart Vision Dedicated Page</div>
        <Step n="1">LEARN → Chart Vision मध्ये जा</Step>
        <Step n="2">Chart screenshot drag-and-drop करा किंवा click करून upload करा</Step>
        <Step n="3">Ctrl+V पण चालतो — clipboard मधून paste करता येते</Step>
        <Step n="4">Quick question buttons मधून निवडा किंवा स्वतःचा question type करा</Step>
        <Step n="5">"Analyze Chart" button click करा</Step>
        <Step n="6">Result → "Save to Knowledge Base" button ने Knowledge Base मध्ये save करता येते</Step>

        <InfoBox color={T.gold} icon="💡" title="JARVIS Chart मध्ये काय ओळखतो?">
          <div>✅ Order Blocks (Bullish + Bearish) — midpoint (Fib 0.5) सह</div>
          <div>✅ Fair Value Gaps (FVGs)</div>
          <div>✅ Liquidity Levels (Equal Highs/Lows)</div>
          <div>✅ BOS / CHoCH (Market Structure)</div>
          <div>✅ Fibonacci Levels (तुमचे personal levels: 0.15/0.75/0.84/0.90)</div>
          <div>✅ तुमच्या Rule Book मधील rules — chart वर apply करतो</div>
          <div>✅ Trade Plan — Entry, SL, TP, Grade</div>
        </InfoBox>

        <InfoBox color={T.green} icon="🎯" title="Best Practice">
          Chart Vision आधी Rule Book मध्ये rules add करा.
          मग screenshot upload केल्यावर JARVIS specifically check करतो:
          "हे Rule R001 (OB Fib 0.5 BUY) apply होते का?"
          Generic analysis पेक्षा हे खूप जास्त specific आणि useful असते.
        </InfoBox>
      </div>
    );

    // ────────────────────────────────────────────────────────────────────
    case 'knowledge': return (
      <div>
        <InfoBox color={T.blue} icon="🧠" title="Knowledge Base — JARVIS ला कसे शिकवायचे?">
          JARVIS जेवढे जास्त knowledge देणार तेवढे चांगले analysis करेल. 3 मार्ग आहेत:
          JSON Upload, Raw Transcript, आणि Pine Script.
        </InfoBox>

        <div style={{ fontSize: '14px', fontWeight: '700', color: T.gold, marginBottom: '10px' }}>
          🏆 Method 1: JSON Upload (सर्वोत्तम) — Claude.ai वापरून
        </div>
        <Step n="1">LEARN → Knowledge Base → <strong style={{color:T.blue}}>🧠 Transcripts</strong> tab उघडा</Step>
        <Step n="2"><strong style={{color:T.gold}}>"📥 Template Download Kara"</strong> button click करा — sample JSON मिळेल</Step>
        <Step n="3"><strong style={{color:T.blue}}>"🤖 Claude.ai Prompt"</strong> button click करा → Prompt copy करा</Step>
        <Step n="4">Claude.ai (claude.ai/chat) वर जा → Prompt paste करा → तुमच्या notes/transcript paste करा</Step>
        <Step n="5">Claude JSON format मध्ये extract करतो — copy करा → .json file म्हणून save करा</Step>
        <Step n="6">JARVIS मध्ये <strong style={{color:T.green}}>"📤 JSON Upload Kara"</strong> → file select → Done!</Step>

        <InfoBox color={T.green} icon="📌" title="JSON मध्ये काय save होते?">
          <div>📌 <strong>Concepts</strong> — Key ideas with definitions</div>
          <div>📏 <strong>Rules</strong> — Actionable entry/exit rules</div>
          <div>📖 <strong>Examples</strong> — Real market examples with prices</div>
          <div>❌ <strong>Mistakes</strong> — Common mistakes + how to avoid</div>
          <div>✅ <strong>Checklist</strong> — Pre-trade checklist items</div>
        </InfoBox>

        <div style={{ fontSize: '14px', fontWeight: '700', color: T.blue, marginBottom: '10px', marginTop: '16px' }}>
          📍 Method 2: Raw Transcript Paste
        </div>
        <Step n="1">Knowledge Base → Transcripts tab मध्ये "YouTube Transcript Add Kara" click करा</Step>
        <Step n="2">Source naam टाका (उदा. "ICT 2022 Mentorship — OB Concepts")</Step>
        <Step n="3">YouTube transcript / notes paste करा (कितीही मोठे)</Step>
        <Step n="4">"Extract & Save" — JARVIS Groq AI वापरून automatically 5 categories extract करतो</Step>

        <div style={{ fontSize: '14px', fontWeight: '700', color: T.purp, marginBottom: '10px', marginTop: '16px' }}>
          📍 Method 3: Pine Script Add करणे
        </div>
        <Step n="1">Knowledge Base → <strong style={{color:T.purp}}>📜 Pine Scripts</strong> tab मध्ये जा</Step>
        <Step n="2">"Navi Script Add Kara" button click करा</Step>
        <Step n="3">Script नाव टाका आणि TradingView Pine Script code paste करा</Step>
        <Step n="4">"JARVIS Analyze & Rules Save Kara" — JARVIS script मधून rules extract करतो</Step>
        <Step n="5">Rules automatically Rule Book मध्ये जातात</Step>

        <InfoBox color={T.gold} icon="🔄" title="JARVIS Chat मध्ये कसे वापरतो?">
          तुम्ही Knowledge Base मध्ये काही save केले की JARVIS Chat मध्ये automatically वापरले जाते.
          उदाहरण: तुम्ही ICT OB notes save केल्यास, chat मध्ये "OB setup आहे" विचारल्यावर
          JARVIS तुमच्या uploaded knowledge मधून relevant concepts search करून reply देतो.
          हे RAG (Retrieval Augmented Generation) system आहे.
        </InfoBox>

        <InfoBox color={T.green} icon="📚" title="Sources — काय काय upload करता येते?">
          <div>✅ YouTube transcript (ICT, Rayner Teo, MentFX, Photon Trading)</div>
          <div>✅ Trading book notes (कोणत्याही book चे)</div>
          <div>✅ स्वतःचे trading notes / observations</div>
          <div>✅ Pine Script strategies (TradingView)</div>
          <div>✅ Chart Vision analysis results (Save to Knowledge Base)</div>
          <div>✅ Daily Recap insights</div>
        </InfoBox>
      </div>
    );

    // ────────────────────────────────────────────────────────────────────
    case 'daily': return (
      <div>
        <InfoBox color={T.gold} icon="📅" title="Daily Recap — JARVIS ला शिकवण्याचा सर्वोत्तम मार्ग">
          दिवसाच्या शेवटी 5 मिनिटे काढा — JARVIS ला आजचा दिवस सांगा.
          JARVIS तुमचे observations process करतो, rules extract करतो,
          आणि उद्याचा plan बनवतो. हे JARVIS चे मुख्य "learning engine" आहे.
        </InfoBox>

        <div style={{ fontSize: '13px', fontWeight: '700', color: T.muted, letterSpacing: '2px', marginBottom: '12px' }}>
          Daily Recap कसा करायचा
        </div>
        <Step n="1">LEARN → Daily Teaching मध्ये जा</Step>
        <Step n="2">"Evening Recap" किंवा "Daily Lesson" section शोधा</Step>
        <Step n="3">खालील fields भरा:</Step>

        {[
          { icon:'📊', name:'Market Behavior', desc:'आज XAU/USD कसे वागले? Bullish/Bearish/Ranging? Major move कुठे झाला?' },
          { icon:'🏛️', name:'Key Levels', desc:'आज कोणते levels important होते? Support/Resistance, OB, FVG prices?' },
          { icon:'🎯', name:'ICT Observations', desc:'आज कोणते ICT concepts दिसले? OB hit झाला? FVG fill झाला? BOS कुठे?' },
          { icon:'✅', name:'What Worked', desc:'आजची strategy / setup जी यशस्वी झाली ती लिहा' },
          { icon:'❌', name:'What Failed', desc:'काय चुकले? का trade lose झाली? Honestly लिहा' },
          { icon:'🧘', name:'Psychology Rating', desc:'1-10 — आज तुमचे emotional state कसे होते?' },
        ].map(f => (
          <div key={f.name} style={{ background: T.card, border: `1px solid ${T.brd}`, borderRadius: '8px', padding: '12px', marginBottom: '8px', display: 'flex', gap: '10px' }}>
            <span style={{ fontSize: '18px', flexShrink: 0 }}>{f.icon}</span>
            <div>
              <div style={{ fontSize: '14px', fontWeight: '700', color: T.blue, marginBottom: '3px' }}>{f.name}</div>
              <div style={{ fontSize: '13px', color: T.text, lineHeight: '1.6' }}>{f.desc}</div>
            </div>
          </div>
        ))}

        <Step n="4">"Submit to JARVIS" button click करा</Step>
        <Step n="5">JARVIS तुमचा recap analyze करतो आणि:</Step>

        <InfoBox color={T.green} icon="🤖" title="JARVIS Daily Recap नंतर काय करतो?">
          <div>✅ नवीन rules extract करतो → Rule Book मध्ये automatically add होतात</div>
          <div>✅ Market behaviors नोंदवतो → Patterns track होतात</div>
          <div>✅ उद्याचे key levels suggest करतो</div>
          <div>✅ तुमच्या Psychology trend track करतो</div>
          <div>✅ Weekly briefing साठी data accumulate होतो</div>
        </InfoBox>

        <InfoBox color={T.blue} icon="📚" title="Daily Teaching">
          Daily Teaching section मध्ये JARVIS रोज एक नवीन ICT/SMC concept शिकवतो.
          हे concepts तुमच्या trading level प्रमाणे personalized असतात.
          Daily Teaching → "Learn Today's Concept" → JARVIS explain करतो.
        </InfoBox>

        <InfoBox color={T.purp} icon="📊" title="Weekly Briefing">
          आठवड्याच्या शेवटी JARVIS स्वतः weekly performance analyze करतो:
          Best setups, worst mistakes, win rate trend, psychology pattern.
          Dashboard → Weekly Briefing section.
        </InfoBox>
      </div>
    );

    // ────────────────────────────────────────────────────────────────────
    case 'psychology': return (
      <div>
        <InfoBox color={T.purp} icon="🧘" title="Psychology Tracker कशासाठी?">
          Trading मध्ये सर्वात मोठा enemy म्हणजे emotions. FOMO, Revenge Trading, Fear —
          हे सगळे psychology issues track केल्यास patterns दिसतात आणि सुधारता येते.
          JARVIS तुमचा emotional data analyze करतो.
        </InfoBox>

        <div style={{ fontSize: '13px', fontWeight: '700', color: T.muted, letterSpacing: '2px', marginBottom: '12px' }}>
          Psychology Tracker कसा वापरायचा
        </div>
        <Step n="1">TRADING → Psychology मध्ये जा</Step>
        <Step n="2">Trading आधी <strong style={{color:T.blue}}>Pre-Trade Check</strong> करा:</Step>

        {[
          { q:'माझी मानसिक स्थिती 1-10 मध्ये किती आहे?', note:'7 पेक्षा कमी असेल तर trade skip करा' },
          { q:'मी आज आधी lose झालो का?', note:'हो असेल तर Revenge Trade चा धोका आहे' },
          { q:'मी rules follow करण्यास ready आहे का?', note:'नाही असेल तर screen बंद करा' },
          { q:'Market मध्ये FOMO वाटतो का?', note:'हो असेल तर setup नीट check करा' },
        ].map((item, i) => (
          <div key={i} style={{ background: 'rgba(167,139,250,0.07)', border: '1px solid rgba(167,139,250,0.2)', borderRadius: '8px', padding: '10px 14px', marginBottom: '8px' }}>
            <div style={{ fontSize: '13px', color: T.text, marginBottom: '4px' }}>❓ {item.q}</div>
            <div style={{ fontSize: '12px', color: T.gold }}>💡 {item.note}</div>
          </div>
        ))}

        <Step n="3">Trading नंतर <strong style={{color:T.blue}}>Post-Trade Log</strong> करा — emotions नोंदवा</Step>
        <Step n="4">JARVIS तुमचे Psychology patterns analyze करतो</Step>

        <InfoBox color={T.red} icon="🚨" title="Trading बंद कधी करायची?">
          <div>❌ Psychology score 5 पेक्षा कमी असेल</div>
          <div>❌ सलग 2 losses झाल्या</div>
          <div>❌ Daily max loss limit hit झाली (Risk Manager मध्ये set केलेली)</div>
          <div>❌ Angry / frustrated / excited वाटत असेल</div>
          <div style={{ marginTop: '8px', color: T.gold }}>💡 JARVIS Chat मध्ये "मी आत्ता trade घेऊ का?" विचारा — psychology पण check करतो.</div>
        </InfoBox>

        <InfoBox color={T.green} icon="✅" title="Healthy Trading Psychology">
          <div>✅ Rule-based trading — emotions नाही</div>
          <div>✅ Fixed risk per trade — कधीही जास्त नाही</div>
          <div>✅ Minimum 1:2 RR — average trades घेऊ नका</div>
          <div>✅ Daily max 3 trades — जास्त नाही</div>
          <div>✅ Loss झाल्यावर screen बंद करा — परत येऊ नका</div>
        </InfoBox>
      </div>
    );

    // ────────────────────────────────────────────────────────────────────
    case 'stats': return (
      <div>
        <InfoBox color={T.blue} icon="📈" title="Statistics Screen — काय दाखवते?">
          तुमच्या सर्व recorded trades चे detailed analysis येथे दिसते.
          Trade Journal मध्ये trades नोंदवल्याशिवाय Stats रिकामे राहतात.
          TRADING → Statistics मध्ये जा.
        </InfoBox>

        <div style={{ fontSize: '13px', fontWeight: '700', color: T.muted, letterSpacing: '2px', marginBottom: '12px' }}>
          Stats मध्ये काय दिसते?
        </div>

        {[
          { icon:'📊', name:'Win Rate (%)', desc:'एकूण trades मध्ये किती % profitable आहेत. ICT methodology नुसार 50%+ असणे पुरेसे आहे जर RR चांगला असेल.' },
          { icon:'💰', name:'Total P&L ($)', desc:'एकूण profit किंवा loss (USD मध्ये). हे Journal मधील पnl_usd field वरून calculate होते.' },
          { icon:'📐', name:'Average R:R', desc:'तुम्ही सरासरी किती R:R achieve करता? 1:2 पेक्षा जास्त असायला हवे.' },
          { icon:'🕐', name:'Best Session', desc:'London, NY, Asian — कोणत्या session मध्ये तुमचा win rate सर्वात जास्त आहे.' },
          { icon:'⭐', name:'Best Setup Grade', desc:'A+, A, B, C — कोणत्या grade च्या trades मध्ये तुम्ही सर्वात profitable आहात.' },
          { icon:'📅', name:'Best Day of Week', desc:'Monday to Friday — कोणत्या दिवशी तुमचे performance सर्वात चांगले आहे.' },
        ].map(s => (
          <div key={s.name} style={{ background: T.card, border: `1px solid ${T.brd}`, borderRadius: '8px', padding: '12px', marginBottom: '8px', display: 'flex', gap: '10px' }}>
            <span style={{ fontSize: '18px', flexShrink: 0 }}>{s.icon}</span>
            <div>
              <div style={{ fontSize: '14px', fontWeight: '700', color: T.blue, marginBottom: '3px' }}>{s.name}</div>
              <div style={{ fontSize: '13px', color: T.text, lineHeight: '1.6' }}>{s.desc}</div>
            </div>
          </div>
        ))}

        <InfoBox color={T.gold} icon="💡" title="Stats कसे Improve करायचे?">
          <div>• Win rate कमी आहे → Setup quality improve करा (A grade पेक्षा कमी घेऊ नका)</div>
          <div>• RR कमी आहे → TP level जास्त दूर ठेवा किंवा partial close करू नका</div>
          <div>• Asian session मध्ये loss जास्त → त्या session मध्ये trade बंद करा</div>
          <div>• D grade trades जिंकत नाहीत → FOMO trades बंद करा</div>
        </InfoBox>

        <InfoBox color={T.green} icon="📤" title="Data Export / Import">
          Settings screen मध्ये "Export All Data" button वापरून सर्व data JSON file मध्ये
          download करता येतो. Backup साठी weekly export करा.
          "Import Data" ने restore पण करता येते.
        </InfoBox>
      </div>
    );

    // ────────────────────────────────────────────────────────────────────
    case 'training': return (
      <div>
        <InfoBox color={T.green} icon="🎓" title="JARVIS कसे शिकतो? — Training Overview">
          JARVIS हे static AI नाही — तुम्ही जेवढे जास्त वापराल तेवढे smarter होतो.
          4 मार्गांनी JARVIS शिकतो — प्रत्येक मार्ग त्याच्या intelligence मध्ये भर टाकतो.
        </InfoBox>

        {[
          {
            icon: '💬', color: T.blue, title: 'Level 1 — Daily Chat',
            desc: 'Chat मध्ये बोललेल्या प्रत्येक conversation मधून JARVIS automatically rules, behaviors आणि patterns extract करतो. तुम्हाला काहीही करायची गरज नाही — chat करत राहा.',
            result: 'Rule Book आणि Behaviors automatically update होतात',
          },
          {
            icon: '📅', color: T.gold, title: 'Level 2 — Daily Recap',
            desc: 'संध्याकाळी Daily Recap submit केल्यावर JARVIS तुमचा संपूर्ण दिवस analyze करतो. Market observations, चुका, यश — सगळे patterns मध्ये बदलतो.',
            result: 'Patterns + Rules + Tomorrow\'s watch levels तयार होतात',
          },
          {
            icon: '🧠', color: T.purp, title: 'Level 3 — Knowledge Upload',
            desc: 'YouTube transcripts, JSON files, Pine Scripts upload केल्यावर JARVIS त्या knowledge ला searchable chunks मध्ये तोडतो. Chat मध्ये relevant question आल्यावर top 5 chunks automatically inject होतात.',
            result: 'Chat मध्ये ICT/Rayner Teo/MentFX knowledge automatically वापरली जाते',
          },
          {
            icon: '📸', color: T.green, title: 'Level 4 — Screenshot Learning',
            desc: 'Chart screenshots upload केल्यावर JARVIS visual patterns शिकतो. Chart Vision मध्ये "Save to Knowledge Base" केल्यावर ते analysis पुढे reference साठी available राहते.',
            result: 'Chart analysis + Rules combo = highly specific setup identification',
          },
        ].map(l => (
          <div key={l.title} style={{ background: `${l.color}09`, border: `1px solid ${l.color}22`, borderRadius: '12px', padding: '16px', marginBottom: '12px' }}>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', marginBottom: '10px' }}>
              <span style={{ fontSize: '24px' }}>{l.icon}</span>
              <div style={{ fontSize: '15px', fontWeight: '800', color: l.color }}>{l.title}</div>
            </div>
            <div style={{ fontSize: '13px', color: T.text, lineHeight: '1.7', marginBottom: '8px' }}>{l.desc}</div>
            <div style={{ fontSize: '12px', color: l.color, fontWeight: '700' }}>✅ Result: {l.result}</div>
          </div>
        ))}

        <InfoBox color={T.gold} icon="📈" title="JARVIS Evolution Stages">
          Dashboard मध्ये JARVIS चा Evolution Stage दिसतो (1 ते 5):
          <div style={{ marginTop: '8px' }}>
            <div>⭐ <strong>Stage 1</strong> — नुकताच सुरू (0 discussions)</div>
            <div>⭐⭐ <strong>Stage 2</strong> — Learning mode (50+ discussions)</div>
            <div>⭐⭐⭐ <strong>Stage 3</strong> — Pattern recognition (200+ discussions)</div>
            <div>⭐⭐⭐⭐ <strong>Stage 4</strong> — Advanced analysis (500+ discussions)</div>
            <div>⭐⭐⭐⭐⭐ <strong>Stage 5</strong> — Elite AI partner (1000+ discussions)</div>
          </div>
        </InfoBox>

        <InfoBox color={T.blue} icon="🗓️" title="Ideal Daily Routine">
          <Step n="1">सकाळी JARVIS Chat मध्ये: "आज XAU/USD काय expect करतोस?"</Step>
          <Step n="2">Trade घेण्यापूर्वी: Confluence Scorer + Chart Vision</Step>
          <Step n="3">Trade नंतर: Journal मध्ये entry</Step>
          <Step n="4">संध्याकाळी: Daily Recap submit करा</Step>
          <Step n="5">आठवड्यातून एकदा: YouTube/Course transcript → JSON → Upload</Step>
        </InfoBox>
      </div>
    );

    default: return null;
  }
}

// ── Main UserGuide component
export default function UserGuide() {
  const [active, setActive] = useState('overview');

  return (
    <div style={{ padding: '20px', maxWidth: '100%', paddingBottom: '80px', color: T.text, fontFamily: 'system-ui, sans-serif' }}>

      {/* Header */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{
          fontSize: '24px', fontWeight: '900', color: T.blue,
          letterSpacing: '2px', fontFamily: 'monospace', marginBottom: '6px',
          textShadow: '0 0 16px rgba(0,212,255,0.4)',
        }}>📖 USER GUIDE</div>
        <div style={{ fontSize: '14px', color: T.muted }}>
          JARVIS Trading AI — संपूर्ण मार्गदर्शिका (Marathi)
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '20px' }}>
        {SECTIONS.map(s => (
          <button
            key={s.id}
            onClick={() => setActive(s.id)}
            style={{
              padding: '8px 14px',
              borderRadius: '20px',
              border: active === s.id ? `1px solid ${T.blue}` : `1px solid ${T.brd}`,
              background: active === s.id ? 'rgba(0,212,255,0.12)' : 'rgba(255,255,255,0.03)',
              color: active === s.id ? T.blue : T.muted,
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: active === s.id ? '700' : '400',
              transition: 'all 0.15s',
              display: 'flex', alignItems: 'center', gap: '6px',
            }}
          >
            <span>{s.emoji}</span>
            <span>{s.label}</span>
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ maxWidth: '780px' }}>
        <SectionContent id={active} />
      </div>
    </div>
  );
}
